/**
 * OmniClinicalBridge useOffline Hook
 * OmniAlera Technologies Holdings Inc.
 * 
 * React hook for managing offline state and data access
 */

import { useState, useEffect, useCallback } from 'react';
import { offlineSyncManager } from './offlineSyncManager';

interface OfflineState {
  isOnline: boolean;
  isFullySynced: boolean;
  lastSync: Date | null;
  syncStatus: {
    medicines: boolean;
    conditions: boolean;
    calculators: boolean;
  };
  storageInfo: {
    used: string;
    available: string;
    percentage: number;
  } | null;
}

interface UseOfflineReturn extends OfflineState {
  syncData: (medicines: any[], conditions: any[], calculators: any[]) => Promise<boolean>;
  getMedicines: <T>() => Promise<T[]>;
  getConditions: <T>() => Promise<T[]>;
  getCalculators: <T>() => Promise<T[]>;
  refreshStatus: () => Promise<void>;
  isSyncing: boolean;
  syncProgress: number;
}

export function useOffline(): UseOfflineReturn {
  const [state, setState] = useState<OfflineState>({
    isOnline: navigator.onLine,
    isFullySynced: false,
    lastSync: null,
    syncStatus: {
      medicines: false,
      conditions: false,
      calculators: false
    },
    storageInfo: null
  });
  
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  
  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      await offlineSyncManager.initialize();
      await refreshStatus();
    };
    init();
  }, []);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true }));
    };
    
    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false }));
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const refreshStatus = useCallback(async () => {
    const status = await offlineSyncManager.checkSyncStatus();
    const storageInfo = await offlineSyncManager.getStorageInfo();
    
    setState(prev => ({
      ...prev,
      isOnline: navigator.onLine,
      isFullySynced: offlineSyncManager.isFullySynced(),
      lastSync: status.lastSync,
      syncStatus: {
        medicines: status.medicines,
        conditions: status.conditions,
        calculators: status.calculators
      },
      storageInfo
    }));
  }, []);
  
  const syncData = useCallback(async (
    medicines: any[],
    conditions: any[],
    calculators: any[]
  ): Promise<boolean> => {
    setIsSyncing(true);
    setSyncProgress(0);
    
    const success = await offlineSyncManager.syncAllData(
      medicines,
      conditions,
      calculators,
      (progress) => {
        const percent = Math.round((progress.current / progress.total) * 100);
        setSyncProgress(percent);
      }
    );
    
    setIsSyncing(false);
    await refreshStatus();
    
    return success;
  }, [refreshStatus]);
  
  const getMedicines = useCallback(<T,>() => {
    return offlineSyncManager.getMedicines<T>();
  }, []);
  
  const getConditions = useCallback(<T,>() => {
    return offlineSyncManager.getConditions<T>();
  }, []);
  
  const getCalculators = useCallback(<T,>() => {
    return offlineSyncManager.getCalculators<T>();
  }, []);
  
  return {
    ...state,
    syncData,
    getMedicines,
    getConditions,
    getCalculators,
    refreshStatus,
    isSyncing,
    syncProgress
  };
}
